import { apiGet, apiPost, apiPut, apiDelete } from '../../Restore/library/helpers/apiHelper';

/**
 * Save or update tech report form data
 * @param {Object} formData - Complete form data object
 * @returns {Promise<Object>} Response with form ID and success status
 */
export const saveTechReportForm = async (formData) => {
  try {
    const response = await apiPost('api/v1/sleep/tech-report/forms', formData);
    
    if (response && response.success) {
      return {
        success: true,
        message: response.message || "Tech report form saved successfully",
        data: response.data,
        form_id: response.form_id
      };
    }
    
    return {
      success: false,
      message: response.message || "Unexpected response format from server"
    };
    
  } catch (error) {
    console.error("Error saving tech report form:", error);
    
    if (error.response) {
      const status = error.response.status;
      const errorData = error.response.data;
      
      switch (status) {
        case 400:
          return {
            success: false,
            message: errorData.message || "Invalid form data provided. Please check all required fields."
          };
        case 404:
          return {
            success: false,
            message: "Sleep service not found. Please contact your administrator."
          };
        case 500:
          return {
            success: false,
            message: "Server error occurred while saving form. Please try again later."
          };
        default:
          return {
            success: false,
            message: errorData.message || `Server error (${status}). Please try again.`
          };
      }
    }
    
    return {
      success: false,
      message: error.message || "Unable to save form. Please check your connection and try again."
    };
  }
};

/**
 * Submit tech report form and generate PDF
 * @param {Object} formData - Complete form data object
 * @returns {Promise<Object>} Response with PDF URL and form ID
 */
export const submitTechReportForm = async (formData) => {
  try {
    // Set form status to submitted
    const submitData = {
      ...formData,
      form_status: 'submitted',
      submitted_at: new Date().toISOString()
    };
    
    const response = await apiPost('api/v1/sleep/tech-report/forms', submitData);
    
    if (response && response.success) {
      return {
        success: true,
        message: response.message || "Tech report form submitted successfully",
        data: response.data,
        form_id: response.form_id,
        pdf_url: response.pdf_url,
        pdf_filename: response.pdf_filename
      };
    }
    
    return {
      success: false,
      message: response.message || "Unexpected response format from server"
    };
    
  } catch (error) {
    console.error("Error submitting tech report form:", error);
    
    if (error.response) {
      const status = error.response.status;
      const errorData = error.response.data;
      
      switch (status) {
        case 400:
          return {
            success: false,
            message: errorData.message || "Invalid form data provided. Please check all required fields."
          };
        case 404:
          return {
            success: false,
            message: "Sleep service not found. Please contact your administrator."
          };
        case 500:
          return {
            success: false,
            message: "Server error occurred while submitting form. Please try again later."
          };
        default:
          return {
            success: false,
            message: errorData.message || `Server error (${status}). Please try again.`
          };
      }
    }
    
    return {
      success: false,
      message: error.message || "Unable to submit form. Please check your connection and try again."
    };
  }
};

/**
 * Get specific tech report form by ID or query parameters
 * @param {Object} params - Query parameters (form_id, user_id, order_id, form_name)
 * @returns {Promise<Object>} Response with form data
 */
export const getTechReportForm = async (params = {}) => {
  try {
    const response = await apiGet('api/v1/sleep/tech-report/forms', params, false);
    
    if (response && (response.success || response.data)) {
      return {
        success: true,
        data: response.data || response,
        message: "Form retrieved successfully"
      };
    }
    
    return {
      success: false,
      message: response.message || "Form not found",
      data: null
    };
    
  } catch (error) {
    console.error("Error fetching tech report form:", error);
    return {
      success: false,
      message: error.message || "Unable to fetch form. Please try again.",
      data: null
    };
  }
};

/**
 * Get all tech report forms with optional filtering
 * @param {Object} params - Query parameters (user_id, form_status, start_date, end_date)
 * @returns {Promise<Object>} Response with array of forms
 */
export const getAllTechReportForms = async (params = {}) => {
  try {
    const response = await apiGet('api/v1/sleep/tech-report/forms/all', params, false);
    
    if (response && (response.success || response.data)) {
      return {
        success: true,
        data: response.data || response,
        message: "Forms retrieved successfully"
      };
    }
    
    return {
      success: false,
      message: response.message || "No forms found",
      data: []
    };
    
  } catch (error) {
    console.error("Error fetching tech report forms:", error);
    return {
      success: false,
      message: error.message || "Unable to fetch forms. Please try again.",
      data: []
    };
  }
};

/**
 * Update form status (draft, submitted, reviewed, approved, rejected)
 * @param {string|number} formId - Form ID
 * @param {string} status - New status
 * @param {string} reviewedBy - User ID of reviewer (optional)
 * @param {string} comments - Review comments (optional)
 * @returns {Promise<Object>} Response with updated form data
 */
export const updateFormStatus = async (formId, status, reviewedBy = null, comments = null) => {
  try {
    const updateData = {
      form_status: status,
      reviewed_by: reviewedBy,
      review_comments: comments,
      reviewed_at: new Date().toISOString()
    };
    
    const response = await apiPut(`api/v1/sleep/tech-report/forms/${formId}/status`, updateData);
    
    if (response && response.success) {
      return {
        success: true,
        message: response.message || "Form status updated successfully",
        data: response.data
      };
    }
    
    return {
      success: false,
      message: response.message || "Unexpected response format from server"
    };
    
  } catch (error) {
    console.error("Error updating form status:", error);
    
    if (error.response) {
      const status = error.response.status;
      const errorData = error.response.data;
      
      switch (status) {
        case 404:
          return {
            success: false,
            message: "Form not found. Please refresh and try again."
          };
        case 400:
          return {
            success: false,
            message: errorData.message || "Invalid status update data."
          };
        case 500:
          return {
            success: false,
            message: "Server error occurred while updating status. Please try again later."
          };
        default:
          return {
            success: false,
            message: errorData.message || `Server error (${status}). Please try again.`
          };
      }
    }
    
    return {
      success: false,
      message: error.message || "Unable to update form status. Please check your connection and try again."
    };
  }
};

/**
 * Delete tech report form
 * @param {string|number} formId - Form ID to delete
 * @returns {Promise<Object>} Response with deletion status
 */
export const deleteTechReportForm = async (formId) => {
  try {
    const response = await apiDelete(`api/v1/sleep/tech-report/forms/${formId}`);
    
    if (response && response.success) {
      return {
        success: true,
        message: response.message || "Form deleted successfully"
      };
    }
    
    return {
      success: false,
      message: response.message || "Unexpected response format from server"
    };
    
  } catch (error) {
    console.error("Error deleting tech report form:", error);
    
    if (error.response) {
      const status = error.response.status;
      const errorData = error.response.data;
      
      switch (status) {
        case 404:
          return {
            success: false,
            message: "Form not found. It may have already been deleted."
          };
        case 403:
          return {
            success: false,
            message: "You don't have permission to delete this form."
          };
        case 500:
          return {
            success: false,
            message: "Server error occurred while deleting form. Please try again later."
          };
        default:
          return {
            success: false,
            message: errorData.message || `Server error (${status}). Please try again.`
          };
      }
    }
    
    return {
      success: false,
      message: error.message || "Unable to delete form. Please check your connection and try again."
    };
  }
};

/**
 * Download PDF of submitted form
 * @param {string|number} formId - Form ID
 * @returns {Promise<Object>} Response with PDF URL or blob
 */
export const downloadFormPDF = async (formId) => {
  try {
    const response = await apiGet(`api/v1/sleep/tech-report/forms/${formId}/pdf`);
    
    if (response && response.success) {
      return {
        success: true,
        pdf_url: response.pdf_url,
        pdf_filename: response.pdf_filename,
        message: "PDF ready for download"
      };
    }
    
    return {
      success: false,
      message: response.message || "PDF not found or not yet generated"
    };
    
  } catch (error) {
    console.error("Error downloading form PDF:", error);
    
    if (error.response) {
      const status = error.response.status;
      const errorData = error.response.data;
      
      switch (status) {
        case 404:
          return {
            success: false,
            message: "Form or PDF not found. Make sure the form has been submitted."
          };
        case 500:
          return {
            success: false,
            message: "Server error occurred while generating PDF. Please try again later."
          };
        default:
          return {
            success: false,
            message: errorData.message || `Server error (${status}). Please try again.`
          };
      }
    }
    
    return {
      success: false,
      message: error.message || "Unable to download PDF. Please check your connection and try again."
    };
  }
};

/**
 * Get form templates/schemas for different form types
 * @param {string} formType - Type of form (e.g., 'polysomnography', 'cpap_titration')
 * @returns {Promise<Object>} Response with form template
 */
export const getFormTemplate = async (formType) => {
  try {
    const response = await apiGet(`api/v1/sleep/tech-report/forms/template/${formType}`);
    
    if (response && (response.success || response.template)) {
      return {
        success: true,
        template: response.template || response.data,
        message: "Template retrieved successfully"
      };
    }
    
    return {
      success: false,
      message: response.message || "Template not found",
      template: null
    };
    
  } catch (error) {
    console.error("Error fetching form template:", error);
    return {
      success: false,
      message: error.message || "Unable to fetch template. Please try again.",
      template: null
    };
  }
}; 